import { AEOLIAN, DORIAN, HIJAZ } from '../track-kit';
import type { DrumPart, MusicPart, MusicTrack } from '../types';
import { hitsAt, singles } from './track-kit';

const TOM_ROLL = [150, 125, 100, 82];

function subParts(
  part: Omit<MusicPart, 'voice' | 'gain'>,
  gain: number,
  sawGain = gain,
): MusicPart[] {
  return [
    { ...part, voice: 'sub', gain },
    { ...part, voice: 'sub-saw', gain: sawGain },
  ];
}

const COUPOLE_SPARKS = singles([
  [3, 9],
  [7, 11],
  [13, 14],
  [18, 12],
  [22, 16],
  [27, 13],
  [30, 18],
  [35, 15],
  [41, 11],
  [46, 14],
  [50, 16],
  [57, 12],
  [61, 19],
]);

const SOUS_LA_COUPOLE: MusicTrack = {
  id: 'sous-la-coupole',
  name: 'Sous la coupole',
  rootMidi: 26,
  scale: DORIAN,
  chords: [0, 3, 0, -1],
  kick: { fromHz: 170, release: 0.3 },
  drums: [
    { voice: 'kick', layer: 'kick', loopSteps: 16, hits: hitsAt([0], 0.8), in: 'rise' },
    { voice: 'kick', layer: 'clap', loopSteps: 16, hits: hitsAt([0, 10]), in: 'drop' },
    { voice: 'rim', layer: 'kick', loopSteps: 16, hits: hitsAt([8], 0.7), in: 'rise' },
    { voice: 'snare', layer: 'clap', loopSteps: 16, hits: hitsAt([8], 0.8) },
    { voice: 'rim', layer: 'clap', loopSteps: 32, hits: hitsAt([19, 30], 0.6), pan: 0.4 },
    {
      voice: 'shaker',
      layer: 'kick',
      loopSteps: 16,
      pan: 0.25,
      hits: [
        [1, 0.6],
        [3, 1],
        [5, 0.6],
        [7, 1],
        [9, 0.6],
        [11, 1],
        [13, 0.6],
        [15, 1],
      ],
    },
    { voice: 'shaker', layer: 'clap', loopSteps: 16, hits: hitsAt([2, 6, 10, 14], 0.6), pan: 0.25 },
  ],
  parts: [
    {
      voice: 'sub',
      layer: 'bass',
      loopSteps: 16,
      octave: 1,
      followsChord: true,
      notes: [[0, 0, 16]],
      in: 'rise',
      gain: 0.75,
    },
    ...subParts(
      {
        layer: 'bass',
        loopSteps: 32,
        octave: 1,
        followsChord: true,
        notes: [
          [0, 0, 6],
          [6, 0, 2],
          [8, 2, 3],
          [11, 1, 1],
          [12, 0, 4],
          [16, 0, 6],
          [22, -1, 2],
          [24, 0, 4],
          [28, 4, 4],
        ],
        in: 'drop',
      },
      0.8,
    ),
    {
      voice: 'bowl',
      layer: 'texture',
      loopSteps: 32,
      octave: 3,
      followsChord: true,
      notes: [[0, 0, 1]],
      in: 'light',
    },
    {
      voice: 'bowl',
      layer: 'texture',
      loopSteps: 32,
      octave: 3,
      followsChord: true,
      notes: [[0, 0, 1]],
      in: 'rise',
    },
    {
      voice: 'bowl',
      layer: 'clap',
      loopSteps: 64,
      octave: 3,
      followsChord: true,
      notes: [[0, 0, 1]],
      gain: 0.7,
    },
    {
      voice: 'droplet',
      layer: 'texture',
      loopSteps: 64,
      octave: 3,
      followsChord: false,
      notes: COUPOLE_SPARKS,
    },
    {
      voice: 'flute',
      layer: 'theme',
      loopSteps: 64,
      octave: 3,
      followsChord: false,
      notes: [
        [0, 7, 6],
        [6, 8, 2],
        [8, 9, 8],
        [16, 11, 6],
        [22, 9, 2],
        [24, 8, 8],
        [32, 7, 6],
        [38, 6, 2],
        [40, 4, 8],
        [48, 5, 4],
        [52, 4, 4],
        [56, 2, 8],
      ],
    },
  ],
};

const SOIE_OUD = singles([
  [0, 7],
  [1, 8],
  [2, 9],
  [3, 8],
  [4, 7],
  [6, 6],
  [7, 7],
  [8, 8],
  [10, 7],
  [11, 6],
  [12, 5],
  [14, 4],
]);

const ROUTE_DE_LA_SOIE: MusicTrack = {
  id: 'route-de-la-soie',
  name: 'Route de la soie',
  rootMidi: 28,
  scale: HIJAZ,
  chords: [0, 1, 0, -1],
  kick: { fromHz: 180, release: 0.22 },
  drums: [
    { voice: 'doum', layer: 'kick', loopSteps: 16, hits: hitsAt([0, 8]) },
    { voice: 'tek', layer: 'kick', loopSteps: 16, hits: hitsAt([2, 12]), pan: 0.2 },
    { voice: 'tek', layer: 'kick', loopSteps: 16, hits: hitsAt([6]), pan: -0.2 },
    { voice: 'tek', layer: 'clap', loopSteps: 16, hits: hitsAt([3, 5, 11, 15], 0.42), pan: 0.35 },
    { voice: 'tek', layer: 'clap', loopSteps: 16, hits: hitsAt([10, 14], 0.42), pan: -0.35 },
    { voice: 'doum', layer: 'texture', loopSteps: 16, hits: hitsAt([0], 0.5), in: 'light' },
    { voice: 'kick', layer: 'kick', loopSteps: 16, hits: hitsAt([0], 0.7), in: 'rise' },
    { voice: 'kick', layer: 'clap', loopSteps: 16, hits: hitsAt([0, 10], 0.95), in: 'drop' },
    { voice: 'clap', layer: 'clap', loopSteps: 16, hits: hitsAt([8], 0.5) },
  ],
  parts: [
    {
      voice: 'round-bass',
      layer: 'bass',
      loopSteps: 16,
      octave: 1,
      followsChord: true,
      notes: [
        [0, 0, 3],
        [3, 0, 1],
        [6, 1, 2],
        [8, 0, 3],
        [11, 2, 1],
        [12, 1, 2],
        [14, 0, 2],
      ],
    },
    {
      voice: 'riq',
      layer: 'clap',
      loopSteps: 16,
      octave: 0,
      followsChord: false,
      notes: singles([
        [4, 0],
        [12, 0],
      ]),
    },
    { voice: 'oud', layer: 'kick', loopSteps: 16, octave: 2, followsChord: false, notes: SOIE_OUD },
    {
      voice: 'oud',
      layer: 'texture',
      loopSteps: 32,
      octave: 2,
      followsChord: false,
      notes: SOIE_OUD,
      in: 'light',
    },
    {
      voice: 'ney',
      layer: 'theme',
      loopSteps: 64,
      octave: 2,
      followsChord: false,
      notes: [
        [0, 11, 4],
        [4, 10, 2],
        [6, 9, 2],
        [8, 8, 4],
        [12, 9, 2],
        [14, 8, 1],
        [15, 9, 1],
        [16, 7, 10],
        [28, 6, 2],
        [30, 7, 2],
        [32, 9, 4],
        [36, 10, 2],
        [38, 11, 2],
        [40, 12, 6],
        [46, 11, 2],
        [48, 10, 2],
        [50, 9, 2],
        [52, 8, 2],
        [54, 9, 1],
        [55, 8, 1],
        [56, 7, 8],
      ],
    },
  ],
};

const DUB_DES_CHAMPIGNONS: MusicTrack = {
  id: 'dub-des-champignons',
  name: 'Dub des champignons',
  rootMidi: 31,
  scale: AEOLIAN,
  chords: [0, 0, -2, -1],
  kick: { fromHz: 160, release: 0.3 },
  echo: { feedback: 0.55, ret: 0.45, tone: 2400 },
  drums: [
    { voice: 'kick', layer: 'kick', loopSteps: 16, hits: hitsAt([8]) },
    { voice: 'kick', layer: 'clap', loopSteps: 16, hits: hitsAt([0]) },
    { voice: 'snare', layer: 'kick', loopSteps: 16, hits: hitsAt([8], 0.9) },
    {
      voice: 'hat',
      layer: 'kick',
      loopSteps: 16,
      hits: hitsAt([2, 6, 10, 14], 0.9),
      pan: 0.2,
      in: 'rise',
    },
    {
      voice: 'hat-open',
      layer: 'clap',
      loopSteps: 16,
      hits: hitsAt([2, 6, 10, 14], 0.9),
      pan: 0.2,
    },
    { voice: 'hat', layer: 'clap', loopSteps: 16, hits: hitsAt([1, 5, 9, 13], 0.6), pan: -0.4 },
    { voice: 'hat', layer: 'clap', loopSteps: 16, hits: hitsAt([3, 7, 11, 15], 0.6), pan: 0.4 },
    { voice: 'rim', layer: 'kick', loopSteps: 64, hits: hitsAt([62, 63], 0.8), pan: -0.3 },
  ],
  parts: [
    ...subParts(
      {
        layer: 'bass',
        loopSteps: 32,
        octave: 0,
        followsChord: true,
        notes: [
          [0, 0, 3],
          [4, 4, 2],
          [6, 2, 2],
          [10, 0, 2],
          [12, -1, 2],
          [14, 0, 2],
          [16, 0, 3],
          [20, 4, 2],
          [22, 5, 1],
          [23, 4, 1],
          [24, 2, 4],
          [28, 0, 4],
        ],
      },
      1.05,
    ),
    {
      voice: 'sub',
      layer: 'texture',
      loopSteps: 32,
      octave: 0,
      followsChord: true,
      notes: [[0, 0, 8]],
      in: 'light',
      gain: 0.7,
    },
    ...[0, 2, 4].flatMap((degree): MusicPart[] => [
      {
        voice: 'skank',
        layer: 'kick',
        loopSteps: 16,
        octave: 3,
        followsChord: true,
        notes: singles([
          [4, degree],
          [12, degree],
        ]),
      },
      {
        voice: 'skank',
        layer: 'texture',
        loopSteps: 16,
        octave: 3,
        followsChord: true,
        notes: singles([[12, degree]]),
        in: 'light',
        gain: 1.2,
      },
    ]),
    {
      voice: 'melodica',
      layer: 'clap',
      loopSteps: 64,
      octave: 2,
      followsChord: false,
      notes: [
        [0, 7, 3],
        [3, 6, 1],
        [4, 7, 4],
        [8, 9, 2],
        [10, 7, 2],
        [12, 6, 4],
        [16, 4, 6],
        [22, 5, 2],
        [24, 6, 4],
        [28, 4, 4],
        [32, 7, 3],
        [35, 6, 1],
        [36, 7, 2],
        [38, 9, 2],
        [40, 10, 6],
        [46, 9, 2],
        [48, 7, 4],
        [52, 6, 2],
        [54, 4, 2],
        [56, 2, 8],
      ],
    },
    {
      voice: 'siren',
      layer: 'texture',
      loopSteps: 64,
      octave: 3,
      followsChord: false,
      notes: [[16, 3, 12]],
      in: 'light',
    },
    {
      voice: 'siren',
      layer: 'clap',
      loopSteps: 128,
      octave: 3,
      followsChord: false,
      notes: [[112, 3, 12]],
    },
  ],
};

const LA_CEREMONIE: MusicTrack = {
  id: 'la-ceremonie',
  name: 'La cérémonie',
  rootMidi: 29,
  scale: AEOLIAN,
  chords: [0, -2, 3, 4],
  kick: { fromHz: 170, release: 0.24 },
  drums: [
    {
      voice: 'frame-drum',
      layer: 'texture',
      loopSteps: 16,
      hits: hitsAt([0], 0.64),
      in: 'light',
      release: 0.6,
    },
    { voice: 'frame-drum', layer: 'kick', loopSteps: 16, hits: hitsAt([0], 0.55) },
    { voice: 'frame-drum', layer: 'kick', loopSteps: 16, hits: hitsAt([3], 0.32) },
    { voice: 'tom', layer: 'kick', loopSteps: 16, hits: hitsAt([8], 0.4), hz: 96, in: 'rise' },
    ...TOM_ROLL.map((hz, index): DrumPart => ({
      voice: 'tom',
      layer: 'clap',
      loopSteps: 64,
      hits: hitsAt([60 + index], 0.4),
      hz,
      release: 0.3,
    })),
    {
      voice: 'rattle',
      layer: 'kick',
      loopSteps: 16,
      pan: 0.3,
      hits: [
        [1, 0.55],
        [3, 1],
        [5, 0.55],
        [7, 1],
        [9, 0.55],
        [11, 1],
        [13, 0.55],
        [15, 1],
      ],
    },
    { voice: 'kick', layer: 'clap', loopSteps: 16, hits: hitsAt([0, 10], 0.95) },
    { voice: 'snare', layer: 'clap', loopSteps: 16, hits: hitsAt([8], 0.75) },
  ],
  parts: [
    ...subParts(
      {
        layer: 'bass',
        loopSteps: 16,
        octave: 0,
        followsChord: true,
        notes: [
          [0, 0, 6],
          [6, 7, 2],
          [8, 0, 4],
          [12, 4, 4],
        ],
      },
      0.75,
      1.34,
    ),
    {
      voice: 'kalimba',
      layer: 'texture',
      loopSteps: 16,
      octave: 3,
      followsChord: true,
      notes: singles([
        [0, 4],
        [2, 7],
        [3, 9],
        [6, 7],
        [8, 4],
        [10, 9],
        [11, 7],
        [14, 2],
      ]),
    },
    ...[0, 2, 4].map((degree): MusicPart => ({
      voice: 'choir',
      layer: 'texture',
      loopSteps: 16,
      octave: 2,
      followsChord: true,
      notes: [[0, degree, 16]],
    })),
    {
      voice: 'choir',
      layer: 'clap',
      loopSteps: 64,
      octave: 3,
      followsChord: false,
      notes: [
        [0, 4, 6],
        [6, 5, 2],
        [8, 4, 4],
        [12, 2, 4],
        [16, 4, 10],
        [26, 2, 2],
        [28, 1, 4],
        [32, 2, 6],
        [38, 4, 2],
        [40, 7, 8],
        [48, 6, 4],
        [52, 5, 4],
        [56, 4, 8],
      ],
    },
  ],
};

const MANDALA_DE_FEU: MusicTrack = {
  id: 'mandala-de-feu',
  name: 'Mandala de feu',
  rootMidi: 25,
  scale: AEOLIAN,
  chords: [0, -2, -4, -1],
  kick: { fromHz: 170, release: 0.28 },
  drums: [
    { voice: 'kick', layer: 'kick', loopSteps: 16, hits: hitsAt([0, 10], 0.85), in: 'rise' },
    { voice: 'snare', layer: 'kick', loopSteps: 16, hits: hitsAt([8], 0.7), in: 'rise' },
    {
      voice: 'kick',
      layer: 'clap',
      loopSteps: 16,
      hits: [
        [0, 1],
        [2, 0.4],
        [5, 0.85],
        [8, 1],
        [10, 0.4],
        [13, 0.85],
      ],
      in: 'drop',
    },
    { voice: 'snare', layer: 'clap', loopSteps: 16, hits: hitsAt([4, 12], 0.7) },
    { voice: 'clap', layer: 'clap', loopSteps: 16, hits: hitsAt([4, 12], 0.35) },
    {
      voice: 'hat',
      layer: 'kick',
      loopSteps: 16,
      hits: hitsAt([4, 8, 12], 0.8),
      pan: 0.2,
      in: 'rise',
    },
    {
      voice: 'hat-open',
      layer: 'kick',
      loopSteps: 16,
      hits: hitsAt([2, 6, 10, 14], 0.8),
      pan: 0.2,
      in: 'rise',
    },
    {
      voice: 'hat-open',
      layer: 'clap',
      loopSteps: 16,
      hits: hitsAt([2, 6, 10, 14], 0.7),
      pan: 0.15,
    },
    {
      voice: 'shaker',
      layer: 'clap',
      loopSteps: 16,
      pan: -0.3,
      hits: [
        [0, 0.8],
        [1, 0.45],
        [4, 0.8],
        [5, 0.45],
        [8, 0.8],
        [9, 0.45],
        [12, 0.8],
        [13, 0.45],
      ],
    },
    {
      voice: 'shaker',
      layer: 'clap',
      loopSteps: 16,
      pan: 0.3,
      hits: [
        [2, 0.8],
        [3, 0.45],
        [6, 0.8],
        [7, 0.45],
        [10, 0.8],
        [11, 0.45],
        [14, 0.8],
        [15, 0.45],
      ],
    },
  ],
  parts: [
    ...subParts(
      {
        layer: 'bass',
        loopSteps: 16,
        octave: 1,
        followsChord: true,
        notes: [
          [0, 0, 8],
          [10, 0, 4],
        ],
        in: 'rise',
      },
      0.8,
    ),
    ...subParts(
      {
        layer: 'bass',
        loopSteps: 16,
        octave: 1,
        followsChord: true,
        notes: [
          [0, 0, 2],
          [3, 0, 1],
          [5, 0, 3],
          [8, 0, 2],
          [11, 2, 1],
          [13, -1, 3],
        ],
        in: 'drop',
      },
      0.95,
    ),
    {
      voice: 'goa-arp',
      layer: 'texture',
      loopSteps: 12,
      octave: 2,
      followsChord: true,
      notes: singles([0, 2, 4, 7, 9, 11, 14, 11, 9, 7, 4, 2].map((degree, step) => [step, degree])),
    },
    {
      voice: 'mist-lead',
      layer: 'clap',
      loopSteps: 64,
      octave: 2,
      followsChord: false,
      notes: [
        [0, 7, 4],
        [4, 9, 2],
        [6, 7, 2],
        [8, 11, 6],
        [14, 9, 2],
        [16, 7, 4],
        [20, 6, 4],
        [24, 4, 8],
        [32, 7, 4],
        [36, 9, 2],
        [38, 11, 2],
        [40, 12, 6],
        [46, 11, 2],
        [48, 9, 4],
        [52, 11, 4],
        [56, 7, 8],
      ],
    },
    {
      voice: 'bowl',
      layer: 'texture',
      loopSteps: 32,
      octave: 3,
      followsChord: true,
      notes: [[0, 0, 1]],
      in: 'light',
      gain: 0.6,
    },
    {
      voice: 'bowl',
      layer: 'clap',
      loopSteps: 64,
      octave: 3,
      followsChord: true,
      notes: [[0, 0, 1]],
      gain: 0.6,
    },
  ],
};

export const DOME_TRACKS: readonly MusicTrack[] = [
  SOUS_LA_COUPOLE,
  ROUTE_DE_LA_SOIE,
  DUB_DES_CHAMPIGNONS,
  LA_CEREMONIE,
  MANDALA_DE_FEU,
];
